
import { supabase } from '../../client';
import { BookingType } from '../../types';
import { BookingCreationData, BookingCreationResult } from './types';
import { sendBookingConfirmation } from '@/lib/email';
import { fetchClassById } from '../classes';

/**
 * Creates a booking for a class using the booking edge function
 */
export const createBooking = async (data: BookingCreationData): Promise<BookingCreationResult> => {
  try {
    console.log("Creating booking with data:", {
      ...data,
      password: data.password ? "[REDACTED]" : undefined
    });
    
    // If the user is trying to create an account, we need to handle that first
    let userId = data.userId;
    let emailVerificationSent = false;
    
    if (data.createAccount && data.password && data.email) {
      try {
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              first_name: data.firstName,
              last_name: data.lastName,
              phone: data.phone,
            },
            emailRedirectTo: `${window.location.origin}/auth/callback?redirectTo=/account`,
          }
        });
        
        if (authError) {
          console.error("Failed to create account:", authError);
          return {
            success: false,
            error: `Account creation failed: ${authError.message}`
          };
        }
        
        if (authData.user) {
          userId = authData.user.id;
          emailVerificationSent = !data.skipEmailVerification && authData.user.identities?.length === 0;
          console.log("Account created, user ID:", userId, "Verification required:", emailVerificationSent);
        }
      } catch (accountError: any) {
        console.error("Error creating account:", accountError);
        return {
          success: false,
          error: `Account creation error: ${accountError.message}`
        };
      }
    }
    
    // Fetch class details for email confirmation
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('*')
      .eq('id', data.classId)
      .single();
    
    if (classError) {
      console.error("Error fetching class:", classError);
      return {
        success: false,
        error: `Class not found: ${classError.message}`
      };
    }
    
    // Determine booking type
    const bookingType = data.giftCardId ? 'gift_card' : 
                        (data.useSubscription && data.useSubscription !== 'pay_individually') ? 'subscription' : 
                        'single';
    
    // Use the booking edge function to create the booking and update class availability
    console.log("Calling booking edge function");
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/booking`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          classId: data.classId,
          userId: userId || null, // Ensure we send null if userId is undefined
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          bookingType: bookingType,
          subscriptionId: data.useSubscription !== 'pay_individually' ? data.useSubscription : null,
          giftCardId: data.giftCardId,
          groupSize: data.groupSize || 1,
          groupNames: data.groupNames,
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || "Booking creation failed");
    }
    
    // Set payment status based on booking type
    const paymentStatus = bookingType === 'single' ? 'pending' : 'paid';
    
    // Send booking confirmation email
    try {
      console.log("Booking completed, sending confirmation email");
      
      // Prepare the baseUrl for email links
      const baseUrl = window.location.origin;
      
      // Send booking confirmation email
      await sendBookingConfirmation({ 
        booking: { 
          id: result.bookingId, 
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          groupSize: data.groupSize
        }, 
        classDetails: {
          title: classData.title,
          day: classData.day,
          date: classData.date,
          time: classData.time,
          duration: classData.duration
        },
        baseUrl
      });
    } catch (emailError: any) {
      console.warn("Failed to send booking confirmation email:", emailError);
      // We don't want to fail the booking if the email fails
    }
    
    return {
      success: true,
      bookingId: result.bookingId,
      userId: userId,
      emailVerificationSent,
      paymentStatus
    };
  } catch (error: any) {
    console.error("Unexpected error during booking creation:", error);
    return {
      success: false,
      error: error.message || "An unexpected error occurred"
    };
  }
};
